import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useEditPage } from "@/hooks/useEditPage";
import EditPageHeader from "@/components/common/EditPageHeader";
import { api, type ApiResponse } from "@/api/client";
import { flightAllotmentCreateSchema } from "@ckgo2/shared/schemas";
import { sortCzech } from "@ckgo2/shared";
import {
  FLIGHT_ALLOTMENT_STATUSES,
  FLIGHT_ALLOTMENT_STATUS_LABELS,
  type FlightAllotmentStatus,
  type User,
} from "@ckgo2/shared/types";
import { formatDate } from "@/lib/format";
import { enumLabel } from "@/i18n/enumLabels";

interface FormData {
  airline: string;
  label: string;
  pnr: string;
  tripDateId: string | null;
  seatsTotal: number;
  pricePerSeat: number | null;
  currency: string;
  depositAmount: number | null;
  depositDeadline: string | null;
  focReleaseDeadline: string | null;
  ticketingDeadline: string | null;
  status: FlightAllotmentStatus;
  assignedTo: string | null;
  notes: string;
}

interface FlightAllotment extends FormData {
  id: string;
}

interface DepartureRow {
  id: string;
  code?: string | null;
  startDate: string;
  tripTitle?: string | null;
}

const FIELD =
  "focus:border-primary-500 focus:ring-primary-500/20 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white";
const LABEL = "mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300";

export default function FlightAllotmentEditPage() {
  const { t } = useTranslation();
  const { data: depData } = useQuery({
    queryKey: ["departures", "all"],
    queryFn: () => api.get<{ data: DepartureRow[] }>("/api/admin/departures?limit=200"),
  });
  const { data: userData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });

  const { isNew, isLoading, form, saveMutation, onSubmit } = useEditPage<FormData, FlightAllotment>(
    {
      resourceKey: "flight-allotment",
      listKey: "flight-allotments",
      apiPath: "/api/admin/flight-allotments",
      schema: flightAllotmentCreateSchema,
      slugSourceField: "label",
      defaultValues: {
        airline: "",
        label: "",
        pnr: "",
        tripDateId: null,
        seatsTotal: 0,
        pricePerSeat: null,
        currency: "CZK",
        depositAmount: null,
        depositDeadline: null,
        focReleaseDeadline: null,
        ticketingDeadline: null,
        status: "active",
        assignedTo: null,
        notes: "",
      },
      navigateTo: "/flights",
      transformOnLoad: (a) => ({
        airline: a.airline || "",
        label: a.label || "",
        pnr: a.pnr || "",
        tripDateId: a.tripDateId ?? null,
        seatsTotal: a.seatsTotal ?? 0,
        pricePerSeat: a.pricePerSeat ?? null,
        currency: a.currency || "CZK",
        depositAmount: a.depositAmount ?? null,
        depositDeadline: a.depositDeadline ?? null,
        focReleaseDeadline: a.focReleaseDeadline ?? null,
        ticketingDeadline: a.ticketingDeadline ?? null,
        status: a.status || "active",
        assignedTo: a.assignedTo ?? null,
        notes: a.notes || "",
      }),
    },
  );

  const { register, formState } = form;
  const departures = depData?.data ?? [];
  // Roletka vlastníka abecedně (`sortCzech`); `/api/admin/users` podle jména neřadí.
  const users = sortCzech(userData?.data ?? [], (u) => u.name);

  if (isLoading) return <p className="p-6 text-gray-500">{t("common.states.loading")}</p>;

  return (
    <form id="allotment-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <EditPageHeader
        backUrl="/flights"
        isNew={isNew}
        newTitle={t("ops.allotmentEdit.newTitle")}
        editTitle={t("ops.allotmentEdit.editTitle")}
        formId="allotment-form"
        isSubmitting={saveMutation.isPending}
        isDirty={formState.isDirty}
        error={saveMutation.error as Error | null}
        validationErrors={formState.errors}
        isSuccess={saveMutation.isSuccess}
      />

      <div className="grid grid-cols-1 gap-5 rounded-xl border border-gray-200 bg-white p-6 md:grid-cols-2 dark:border-gray-700 dark:bg-gray-800">
        <div>
          <label className={LABEL}>{t("ops.allotmentEdit.airline")}</label>
          <input className={FIELD} placeholder="Vietnam Airlines" {...register("airline")} />
        </div>
        <div>
          <label className={LABEL}>{t("ops.allotmentEdit.routeLabel")}</label>
          <input className={FIELD} placeholder="PRG–DOH–HAN" {...register("label")} />
        </div>
        <div>
          <label className={LABEL}>{t("ops.allotmentEdit.blockPnr")}</label>
          <input className={FIELD} placeholder="ABC123" {...register("pnr")} />
        </div>
        <div>
          <label className={LABEL}>{t("ops.allotmentEdit.departure")}</label>
          <select className={FIELD} {...register("tripDateId")}>
            <option value="">{t("ops.shared.noneOption")}</option>
            {departures.map((d) => (
              <option key={d.id} value={d.id}>
                {(d.code ? `${d.code} · ` : "") + formatDate(d.startDate)}
                {d.tripTitle ? ` · ${d.tripTitle}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={LABEL}>{t("ops.allotmentEdit.seatsTotal")}</label>
          <input
            type="number"
            className={FIELD}
            {...register("seatsTotal", { valueAsNumber: true })}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>{t("ops.allotmentEdit.pricePerSeat")}</label>
            <input
              type="number"
              className={FIELD}
              {...register("pricePerSeat", { setValueAs: (v) => (v === "" ? null : Number(v)) })}
            />
          </div>
          <div>
            <label className={LABEL}>{t("common.fields.currency")}</label>
            <input className={FIELD} maxLength={3} {...register("currency")} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>{t("ops.allotmentEdit.depositAmount")}</label>
            <input
              type="number"
              className={FIELD}
              {...register("depositAmount", { setValueAs: (v) => (v === "" ? null : Number(v)) })}
            />
          </div>
          <div>
            <label className={LABEL}>{t("ops.allotmentEdit.depositDeadline")}</label>
            <input type="date" className={FIELD} {...register("depositDeadline")} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL}>{t("ops.allotmentEdit.focCancellation")}</label>
            <input type="date" className={FIELD} {...register("focReleaseDeadline")} />
          </div>
          <div>
            <label className={LABEL}>{t("ops.allotmentEdit.ticketingDeadline")}</label>
            <input type="date" className={FIELD} {...register("ticketingDeadline")} />
          </div>
        </div>

        <div>
          <label className={LABEL}>{t("common.fields.status")}</label>
          <select className={FIELD} {...register("status")}>
            {FLIGHT_ALLOTMENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {enumLabel(FLIGHT_ALLOTMENT_STATUS_LABELS, s)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL}>{t("ops.allotmentEdit.owner")}</label>
          <select className={FIELD} {...register("assignedTo")}>
            <option value="">{t("ops.allotmentEdit.unassignedOption")}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className={LABEL}>{t("common.fields.notes")}</label>
          <textarea rows={3} className={FIELD} {...register("notes")} />
        </div>
      </div>
    </form>
  );
}
